import React, { useCallback, useContext, useRef, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import UniversalCreateButton from '@atlassian/universal-create';

import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { fg } from '@confluence/feature-gating';
import { START } from '@confluence/navdex/entry-points/NavdexPointType';
import { AppNavigationContext } from '@confluence/app-navigation-context';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { useLiveDocChangeboarding } from '@confluence/onboarding-live-doc-changeboarding/entry-points/useLiveDocChangeboarding';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	checkShouldShowLozenge,
	iterateLozengeShownTracker,
} from '@confluence/live-pages-utils/entry-points/liveDocsLozengeUtils';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { useSpaceEnabledContentTypes } from '../CreateContentPopover/useSpaceEnabledContentTypes';

import { useUniversalCreateState, useUniversalCreateActions } from './useUniversalCreate';
import { useMenuItems } from './useMenuItems';

const messages = defineMessages({
	create: {
		id: 'create-blank-fabric-page.universal-create.create.text',
		defaultMessage: 'Create',
		description: 'The Create text that appears in the top nav',
	},
});

export const getCreateButtonTheme = (createButtonTheme) => (current, props) => {
	const { buttonStyles, spinnerStyles } = current(props);
	return {
		buttonStyles: {
			...buttonStyles,
			...createButtonTheme.default,
			margin: 0,
			fontWeight: token('font.weight.medium'),
			':hover': createButtonTheme.hover,
			':focus': createButtonTheme.focus,
			// :active doesn't work in FF, because we do a
			// e.preventDefault() on mouse down in Button.
			// '&&' is required to add more CSS specificity
			// && it not a valid CSSObject property
			// @ts-ignore
			'&&': {
				...(props.state === 'active' && createButtonTheme.active),
			},
		},
		spinnerStyles,
	};
};

const Create = ({ flags }) => {
	const { isOpen } = useUniversalCreateState();
	const { setIsOpen, setOpenFrom } = useUniversalCreateActions();
	const items = useMenuItems(flags, () => setIsOpen(false));
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { match } = useContext(RoutesContext);
	const { theme } = useContext(AppNavigationContext);
	const isNav4Enabled = useIsNav4Enabled();
	const isOnEditRoute = useIsEditorPage();
	const { formatMessage } = useIntl();
	const {
		introSpotlight: { isVisible: introSpotlightVisible },
	} = useLiveDocChangeboarding();
	const { cloudId, orgId, userId } = useSessionData();
	const [spaceKey] = usePageSpaceKey();
	const { isCreateLiveDocEnabled } = useSpaceEnabledContentTypes(spaceKey);

	const hasIteratedShownTracker = useRef<boolean>(false);
	// shouldShowLozenge should be stabilized so it is not checked each re-render
	// we also do not want the lozenge to suddenly disappear if it's already shown
	const shouldShowLozenge = useMemo(() => checkShouldShowLozenge(), []);

	const { data } = useGetOnboardingState(
		[AIFC_ONBOARDING_STATE_KEYS.isAIFCSpotlightActive],
		!fg('aifc_onboarding_killswitch') || !fg('aifc_create_enabled'),
	);
	const { isAIFCSpotlightActive } = deserializeState(data);

	const buttonTheme = theme?.mode?.create ? getCreateButtonTheme(theme.mode.create) : undefined;

	const onCreateClick = useCallback(() => {
		if (!isOpen) {
			void setIsOpen(true);
			if (shouldShowLozenge && isCreateLiveDocEnabled && !hasIteratedShownTracker.current) {
				iterateLozengeShownTracker();
				hasIteratedShownTracker.current = true;
			}
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'globalCreateContentPopover',
				source: 'globalNavigation',
				attributes: {
					selectedItemPageContext: match?.name,
					navigationLayer: 'global',
					navigationContainer: 'top',
					navVersion: isNav4Enabled ? '4' : '3',
					navdexPointType: START,
					isOnEditRoute,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		isNav4Enabled,
		isOnEditRoute,
		match?.name,
		isOpen,
		setIsOpen,
		shouldShowLozenge,
		isCreateLiveDocEnabled,
	]);

	//Reset the openFrom state when the create menu is closed
	const onClose = () => {
		void setIsOpen(false);
		void setOpenFrom('');
	};

	return (
		<UniversalCreateButton
			items={items}
			onClick={onCreateClick}
			triggerButtonSpotlightTarget="top-nav-create"
			enableTriggerPulse={false}
			isNav4Enabled={isNav4Enabled}
			isOpen={isOpen}
			onClose={onClose}
			product="confluence"
			buttonTheme={buttonTheme}
			testId="create-content-popover-button"
			preventClose={
				isAIFCSpotlightActive || (introSpotlightVisible && fg('cc_modernize_ga_changeboarding'))
			}
			suggestionConfig={
				fg('navx_uc_enable_suggestion_in_confluence')
					? {
							enabled: true,
							compact: true,
							cloudId: cloudId || '',
							orgId: orgId || '',
							accountId: userId || '',
						}
					: {
							enabled: false,
						}
			}
			{...(fg('platform_navx_universal_create_a11y_fix') && { shouldRenderToParent: true })}
		>
			{formatMessage(messages.create)}
		</UniversalCreateButton>
	);
};

const CreateWithFlags = withFlags(Create);

export function UniversalCreate() {
	return (
		<TransparentErrorBoundary attribution={Attribution.NAVX}>
			<CreateWithFlags />
		</TransparentErrorBoundary>
	);
}
